import { logger } from './logger';
const createAnchor = () => document.createElement('a');

const readBlobAsDataUrl = (blob: Blob): Promise<string> => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onerror = () => reject(reader.error ?? new Error('Failed to read blob as data URL'));
        reader.onloadend = () => {
            if (typeof reader.result === 'string') {
                resolve(reader.result);
            } else {
                reject(new Error('Unsupported reader result type'));
            }
        };
        reader.readAsDataURL(blob);
    });
};

const openDataUrl = (dataUrl: string, filename: string) => {
    const newWindow = window.open(dataUrl, '_blank');
    if (newWindow) {
        try {
            newWindow.document.title = filename;
        } catch {
            // Ignore cross-origin restrictions when setting title
        }
        return;
    }

    const fallbackLink = createAnchor();
    fallbackLink.href = dataUrl;
    fallbackLink.target = '_blank';
    fallbackLink.rel = 'noopener';
    document.body.appendChild(fallbackLink);
    fallbackLink.click();
    document.body.removeChild(fallbackLink);
};

const tryNativeShare = async (blob: Blob, filename: string) => {
    const nav = navigator as Navigator & {
        canShare?: (data?: ShareData) => boolean;
        share?: (data?: ShareData) => Promise<void>;
    };

    if (!nav.canShare || !nav.share) {
        return false;
    }

    const file = new File([blob], filename, { type: blob.type || 'application/octet-stream' });

    if (!nav.canShare({ files: [file] })) {
        return false;
    }

    try {
        await nav.share({ files: [file], title: filename });
        return true;
    } catch (error) {
        logger.warn('Native share cancelled or failed', error);
        return false;
    }
};

export const downloadBlob = async (blob: Blob, filename: string) => {
    const anchor = createAnchor();
    const supportsDownload = typeof anchor.download !== 'undefined';

    if (supportsDownload) {
        const url = window.URL.createObjectURL(blob);
        anchor.href = url;
        anchor.download = filename;
        anchor.rel = 'noopener';
        document.body.appendChild(anchor);
        anchor.click();
        document.body.removeChild(anchor);
        window.URL.revokeObjectURL(url);
        return;
    }

    if (await tryNativeShare(blob, filename)) {
        return;
    }

    try {
        const dataUrl = await readBlobAsDataUrl(blob);
        openDataUrl(dataUrl, filename);
    } catch (error) {
        logger.error('Fallback download failed', error);
        const url = window.URL.createObjectURL(blob);
        window.location.href = url;
        setTimeout(() => window.URL.revokeObjectURL(url), 0);
    }
};
